import { Injectable, Injector } from "@angular/core";
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: "root",
})
export class DWSLanguagesService {
  // ADD HERE THE LANGUAGES TO BE SUPPORTED IN DWS FRONTEND
  //private LANGUAGES = ["it", "en", "de"];
  private LANGUAGES = ['it','en','de', 'fr','es','pt','el','hr'];
  private FALLBACK_LANG = "en"; // English as fallback language

  // ADD HERE THE EXPERIENCE LANGUAGES TO BE SUPPORTED IN DWS
  private EXPERIENCE_LANGUAGES = [
    "ar",
    "bg",
    "da",
    "de",
    "el",
    "en",
    "es",
    "fr",
    "hr",
    "it",
    "nl",
    "pl",
    "pt",
    "ro",
    "ru",
    "zh"
  ];

  // ADD HERE THE CURRENCIES TO BE SUPPORTED IN DWS
  private CURRENCIES = ["EUR", "USD", "CHF"];
  private CURRENCIES_SYMBOLS: { [key: string]: string } = {
    EUR: "€",
    USD: "$",
    CHF: "CHF",
  };

  constructor(private injector: Injector) {}

  /**
   * Custom translation function to avoid circular dependency with @util/tr
   */
  private translate(message: string): string {
    try {
      const translateService: TranslateService = this.injector.get(TranslateService);
      
      // Get current and fallback languages
      const currentLang = this.getCurrentLanguageIso();
      const fallbackLang = this.getFallbackLanguageIso();
      
      // Try to get the translation using the current language first
      let translation = translateService.instant(message);
      
      // If translation is missing (same as key) and we can try fallback
      if (translation === message && currentLang !== fallbackLang) {
        // Check if we have the fallback language translations loaded
        const fallbackTranslations = translateService.translations[fallbackLang];
        if (fallbackTranslations && fallbackTranslations[message]) {
          translation = fallbackTranslations[message];
        }
      }
      
      return translation;
      
    } catch (error) {
      // Fallback to original message if any error occurs
      //console.warn('Translation service error:', error);
      return message;
    }
  }

  list() {
    return this.LANGUAGES.map((lang) => {
      return { iso: lang, name: this.translate(`lang_${lang}`) };
    });
  }

  getCurrentLanguageIso() {
    const lang = localStorage.getItem("locale");
    if (lang && this.LANGUAGES.includes(lang)) {
      return lang;
    } else {
      return this.getFallbackLanguageIso();
    }
  }

  normalizeLanguageIso(lang: string | null | undefined): string | null {
    if (!lang) {
      return null;
    }
    const iso = String(lang).trim().toLowerCase().split(/[-_]/)[0];
    return this.LANGUAGES.includes(iso) ? iso : null;
  }

  accountLanguageIso(account: { language?: string; locale?: string } | null | undefined): string | null {
    if (!account) {
      return null;
    }
    return this.normalizeLanguageIso(account.language || account.locale);
  }

  applyAccountLanguage(account: { language?: string; locale?: string } | null | undefined): boolean {
    const iso = this.accountLanguageIso(account);
    if (!iso || this.getCurrentLanguageIso() === iso) {
      return false;
    }
    this.setCurrentLanguage(iso);
    try {
      this.injector.get(TranslateService).use(iso);
    } catch {
      // TranslateService may be unavailable outside the web app
    }
    return true;
  }

  setCurrentLanguage(lang: string) {
    if (this.LANGUAGES.includes(lang)) {
      localStorage.setItem("locale", lang);
    }
  }

  getCurrentLanguageName() {
    const lang = this.getCurrentLanguageIso();
    return this.list().find((l) => l.iso === lang)?.name;
  }

  getFallbackLanguageIso() {
    return this.FALLBACK_LANG;
  }

  getExperienceLanguagesIso() {
    return this.EXPERIENCE_LANGUAGES;
  }

  getExperienceLanguages() {
    return this.getExperienceLanguagesIso().map((lang) => {
      return { iso: lang, name: this.translate(`lang_${lang}`) };
    });
  }

  getCurrencyCodes() {
    return this.CURRENCIES.map((currency) => {
      return currency.toLowerCase();
    });
  }

  getCurrencies() {
    return this.CURRENCIES.map((currency) => ({
      iso: currency,
      name: this.translate(`currency_${currency.toLowerCase()}`),
      symbol: (this.CURRENCIES_SYMBOLS as any)[currency],
    }));
  }

  getCurrencySymbol(currencyIso: string): string {
    return this.CURRENCIES_SYMBOLS[currencyIso.toUpperCase()] || "";
  }

  getCurrencyDefault() {
    return this.CURRENCIES[0];
  }

  getTranslation(translatable: any): string {
    const lang = this.getCurrentLanguageIso().toUpperCase();
    const fallbackLang = this.getFallbackLanguageIso().toUpperCase();

    if (!translatable) return "";

    // Normalize: if object has a 'translations' field, use that
    const possibleArray = Array.isArray(translatable)
      ? translatable
      : translatable?.translations ||
        translatable?.titleTranslations?.translations;

    // Case 1: It's a translations array
    if (Array.isArray(possibleArray)) {
      const getValue = (entry: any) =>
        entry?.text || entry?.label || entry?.shortLabel || entry?.longText;

      const match = possibleArray.find(
        (t) =>
          t.languageIsoCode?.toUpperCase?.() === lang ||
          t.languageISO?.toUpperCase?.() === lang
      );
      const fallback = possibleArray.find(
        (t) =>
          t.languageIsoCode?.toUpperCase?.() === fallbackLang ||
          t.languageISO?.toUpperCase?.() === fallbackLang
      );

      return getValue(match) || getValue(fallback) || "";
    }

    // Case 2: Plain object with language keys
    if (typeof translatable === "object" && !Array.isArray(translatable)) {
      const keys = Object.keys(translatable);
      const matchKey = keys.find((k) => k.toUpperCase() === lang);
      const fallbackKey = keys.find((k) => k.toUpperCase() === fallbackLang)!;

      return (
        (matchKey && translatable[matchKey]) ||
        translatable[lang] ||
        translatable[fallbackKey] ||
        ""
      );
    }

    return "";
  }

  /**
   * Maps language codes to country codes for flag display
   * Language codes (ISO 639-1) to Country codes (ISO 3166-1 alpha-2)
   */
  getCountryCodeForLanguage(languageCode: string): string {
    const languageToCountryMap: { [key: string]: string } = {
      'en': 'gb', // English -> Great Britain
      'el': 'gr', // Greek -> Greece
      'it': 'it', // Italian -> Italy
      'fr': 'fr', // French -> France
      'de': 'de', // German -> Germany
      'es': 'es', // Spanish -> Spain
      'pt': 'pt', // Portuguese -> Portugal
      'hr': 'hr', // Croatian -> Croatia
      'ar': 'ae', // Arabic -> United Arab Emirates
      'bg': 'bg', // Bulgarian -> Bulgaria
      'da': 'dk', // Danish -> Denmark
      'zh': 'cn', // Chinese -> China
      'ru': 'ru', // Russian -> Russia
      'pl': 'pl', // Polish -> Poland
      'ro': 'ro', // Romanian -> Romania
    };
    
    return languageToCountryMap[languageCode] || languageCode;
  }

  /**
   * Maps 2-letter language ISO codes to 3-letter suffixes used in template types
   * Example: it -> ita, en -> eng
   */
  mapToLangSuffix(languageIso2: string): string {
    const map: { [k: string]: string } = {
      'it': 'ita',
      'en': 'eng',
      'de': 'deu',
      'fr': 'fra',
      'es': 'spa',
      'pt': 'prt',
      'el': 'ell',
      'hr': 'hrv',
    };
    return map[languageIso2] || languageIso2;
  }

  /**
   * Maps 3-letter language codes back to 2-letter ISO codes
   * Example: ita -> it, eng -> en
   */
  mapFromLangSuffix(languageIso3: string): string {
    const reverseMap: { [k: string]: string } = {
      'ita': 'it',
      'eng': 'en',
      'deu': 'de',
      'fra': 'fr',
      'spa': 'es',
      'prt': 'pt',
      'ell': 'el',
      'hrv': 'hr',
    };
    return reverseMap[languageIso3] || languageIso3;
  }
}
